// Shared helpers + small reusable bits for the Workforce Planning module.
//
// Originally this lived inline at the top of PlanningPage.js — extracting
// keeps each tab/dialog file independent and testable.

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ChevronDown } from 'lucide-react';
import { enqueue, shouldQueue } from '@/services/offlineQueue';
import { isNetworkLikeError, safeFetchJson, safeFetchBlob } from '@/utils/safeFetch';

export const API = process.env.REACT_APP_BACKEND_URL;
export const token = () => localStorage.getItem('auth_token') || sessionStorage.getItem('auth_token');

export const api = async (path, opts = {}) => {
  const fullUrl = `${API}/api${path}`;
  const method = (opts.method || 'GET').toUpperCase();

  // For GETs (the heavy data views: actuals, briefing, accuracy, summary,
  // schedule, employees …) we fall back to XHR when fetch trips on a
  // stale Service Worker or a browser-extension hijack. Mutations stay
  // on the existing offline-queue path because they can have side
  // effects we don't want to silently retry over a different transport.
  if (method === 'GET' && !opts.body) {
    try {
      const data = await safeFetchJson(fullUrl, {
        token: token(),
        headers: opts.headers,
      });
      try { localStorage.setItem('kk-last-sync', String(Date.now())); } catch { /* quota */ }
      return data;
    } catch (e) {
      if (isNetworkLikeError(e)) throw new Error('Network error — could not reach server');
      throw e;
    }
  }

  let r;
  try {
    r = await fetch(fullUrl, {
      ...opts,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}`, ...(opts.headers || {}) },
    });
  } catch {
    if (shouldQueue(fullUrl, opts)) {
      enqueue({
        url: fullUrl,
        method,
        body: opts.body,
        label: `${method} ${path}`,
      });
      return { __queued: true, message: 'Saved offline — will sync when back online' };
    }
    throw new Error('Network error — could not reach server');
  }
  if (!r.ok) {
    const e = await r.json().catch(() => ({}));
    // FastAPI HTTPException usually puts a string under `detail`. Validation
    // errors put a list of objects under `detail` instead — flatten those to
    // a readable message so the toast never collapses to a bare "HTTP 400".
    let msg;
    if (typeof e.detail === 'string') {
      msg = e.detail;
    } else if (Array.isArray(e.detail)) {
      msg = e.detail.map((d) => d.msg || JSON.stringify(d)).join('; ');
    } else if (e.detail) {
      msg = JSON.stringify(e.detail);
    } else {
      msg = `HTTP ${r.status}`;
    }
    const err = new Error(msg);
    err.status = r.status;
    err.detail = e.detail;
    throw err;
  }
  try { localStorage.setItem('kk-last-sync', String(Date.now())); } catch { /* quota */ }
  return r.status === 204 ? null : r.json();
};

// Download a binary file (xlsx, pdf …) with the same XHR-fallback
// safety net as `api()`. Returns a Blob so the caller can trigger
// the download itself.
export const apiBlob = async (path) => {
  return safeFetchBlob(`${API}/api${path}`, { token: token() });
};

export const WEEK_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export const fmtMins = (m) => {
  if (!m || m <= 0) return '—';
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${h}:${String(mm).padStart(2, '0')}`;
};

// Compute ISO week id for a given date (YYYY-Www)
export const isoWeek = (date) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

// Planning is locked-in by Wednesday 13:00 server local time. Anything
// drafted AFTER that cutoff implicitly belongs to the *following* week,
// not the current one. So when the page first opens we want the planner
// to land on whichever week they're effectively writing for.
export const PLANNING_CUTOFF_DAY = 3; // 1 = Mon, 2 = Tue, 3 = Wed
export const PLANNING_CUTOFF_HOUR = 13;
export const isPastPlanningCutoff = (now = new Date()) => {
  const dow = now.getDay() || 7; // 1..7 with Sunday = 7
  if (dow > PLANNING_CUTOFF_DAY) return true;
  if (dow < PLANNING_CUTOFF_DAY) return false;
  return now.getHours() >= PLANNING_CUTOFF_HOUR;
};
export const effectivePlanningWeek = (now = new Date()) => {
  const ref = new Date(now);
  if (isPastPlanningCutoff(now)) ref.setDate(ref.getDate() + 7);
  return isoWeek(ref);
};
// Monday and Sunday calendar dates (DD.MM.YYYY) for an ISO week id.
export const weekBounds = (weekId) => {
  const m = /^(\d{4})-W(\d{2})$/.exec(weekId || '');
  if (!m) return null;
  const year = Number(m[1]);
  const wk = Number(m[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const monday = new Date(jan4);
  monday.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1) + (wk - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const fmt = (d) => `${String(d.getUTCDate()).padStart(2, '0')}.${String(d.getUTCMonth() + 1).padStart(2, '0')}.${d.getUTCFullYear()}`;
  return { monday: fmt(monday), sunday: fmt(sunday), weekNumber: wk, year };
};

// Multi-select dropdown used by the Export tab.
export const MultiCheckSelect = ({ label, options, selected, onChange, testId, keyFn = (o) => o.id, labelFn = (o) => o.label }) => {
  const [open, setOpen] = useState(false);
  const toggle = (k) => onChange(selected.includes(k) ? selected.filter(x => x !== k) : [...selected, k]);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-1" data-testid={testId}>
          {label} {selected.length > 0 && <Badge variant="secondary" className="ml-1">{selected.length}</Badge>}
          <ChevronDown className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 max-h-80 overflow-y-auto p-2">
        {options.length === 0 ? <p className="text-xs text-muted-foreground p-2">No options</p> : options.map(o => {
          const k = keyFn(o);
          return (
            <label key={k} className="flex items-center gap-2 p-2 hover:bg-muted/60 rounded cursor-pointer">
              <Checkbox checked={selected.includes(k)} onCheckedChange={() => toggle(k)} />
              <span className="text-sm">{labelFn(o)}</span>
            </label>
          );
        })}
        {selected.length > 0 && (
          <Button size="sm" variant="ghost" onClick={() => onChange([])} className="w-full mt-1">Clear</Button>
        )}
      </PopoverContent>
    </Popover>
  );
};

// Inline notes icon (kept inline to avoid pulling another lucide import in DriversTab)
export const NotesIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="8" y="2" width="8" height="4" rx="1" ry="1"/><path d="M9 14l2 2 4-4"/><path d="M20 7v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2"/></svg>
);
